import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Send, Loader2, ListPlus, History, Music,
  Plus, MessageCircle, Disc3, ChevronRight, ExternalLink,
  TrendingUp, AlertTriangle, CheckCircle2, Zap,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { computeSetWarnings, computeQualityScore, summarizeWarnings } from '@/utils/setWarnings';
import MixQualityCard from '@/components/MixQualityCard';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'cratelab_dj_session';
const newSessionId = () =>
  `dj-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const QUICK_STARTS = [
  { label: 'Warm-up set',   prompt: 'Help me build a warm-up set. I want it to start chilled and slowly build energy.' },
  { label: 'Peak-time set', prompt: 'Help me build a peak-time set, around 60 minutes.' },
  { label: 'Afterhours',    prompt: 'Help me build an afterhours / deep set that grooves but stays controlled.' },
  { label: 'Closing set',   prompt: 'Help me build a closing set — memorable, emotional, leaves people happy.' },
];

const BRIEF_REGEX = /<SET_BRIEF>([\s\S]*?)<\/SET_BRIEF>/;

// ─── Mix Inspector Panel ──────────────────────────────────────────────────
function MixInspectorPanel({ tracks, warnings, curve }) {
  const energyData = (tracks || []).map((t, i) => {
    let e = Number(t?.analysis?.energy) || 0;
    if (e > 1.5) e = e / 100;
    return {
      name: `#${i + 1}`,
      energy: Math.round(e * 100),
      bpm: Math.round(Number(t?.analysis?.bpm) || 0),
      camelot: t?.analysis?.camelot || '?',
      title: t?.title || 'Track',
    };
  });

  const sums = summarizeWarnings(warnings);
  const { score, verdict, tone } = computeQualityScore(warnings);

  const toneColor = tone === 'green' ? '#10B981' : tone === 'blue' ? '#2563FF' : tone === 'amber' ? '#F59E0B' : '#EF4444';

  return (
    <div className="mt-3 space-y-3 border-t border-[#E2E8F0] pt-3">
      {/* Mix quality score */}
      <MixQualityCard warnings={warnings} trackCount={tracks?.length || 0} variant="compact" celebrate={score >= 85} />

      {/* Energy flow chart */}
      {energyData.length >= 2 && (
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8] mb-1.5 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Energy flow ({curve || 'build'})
          </p>
          <div className="h-16 w-full rounded bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={energyData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                <defs>
                  <linearGradient id="setEnergyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={toneColor} stopOpacity={0.7} />
                    <stop offset="95%" stopColor={toneColor} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="energy" stroke={toneColor} strokeWidth={1.5}
                  fill="url(#setEnergyGrad)" isAnimationActive={true} animationDuration={600} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8] mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Transition warnings ({warnings.length})
          </p>
          <div className="space-y-1 max-h-28 overflow-y-auto">
            {warnings.map((w, i) => (
              <div key={i} className={`flex items-start gap-2 px-2 py-1.5 text-[11px] rounded border ${
                w.severity === 'high' ? 'bg-red-50 border-red-200 text-red-700'
                : w.severity === 'med' ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}>
                <span className={`flex-shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full ${
                  w.severity === 'high' ? 'bg-red-500' : w.severity === 'med' ? 'bg-amber-500' : 'bg-blue-500'
                }`} />
                <span className="font-body leading-tight">{w.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {warnings.length === 0 && (
        <div className="flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded">
          <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
          No transition warnings — all keys, BPMs and energy levels flow cleanly
        </div>
      )}
    </div>
  );
}

function extractBrief(text) {
  const m = text.match(BRIEF_REGEX);
  if (!m) return null;
  try { return JSON.parse(m[1].trim()); }
  catch { return null; }
}

function cleanReply(text) {
  return text.replace(BRIEF_REGEX, '').trim();
}

export default function AssistantPage() {
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingBrief, setPendingBrief] = useState(null);
  const [building, setBuilding] = useState(false);
  const [lastSet, setLastSet] = useState(null); // {playlist, tracks}
  const [libraryCtx, setLibraryCtx] = useState(null);
  const [sessions, setSessions] = useState([]);
  const scrollerRef = useRef(null);

  // Initial setup: session id + library context + saved sessions
  useEffect(() => {
    let sid = localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(STORAGE_KEY, sid);
    }
    setSessionId(sid);

    axios.get(`${API}/dj/library-context`)
      .then((r) => setLibraryCtx(r.data))
      .catch(() => { /* ignore */ });
    axios.get(`${API}/dj/sessions`)
      .then((r) => setSessions(r.data.sessions || []))
      .catch(() => { /* ignore */ });
  }, []);

  // Load message history when sessionId changes
  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/dj/sessions/${sessionId}`)
      .then((r) => {
        const msgs = (r.data.messages || []).map((m) => ({
          role: m.role,
          content: m.role === 'assistant' ? cleanReply(m.content) : m.content,
          brief: m.role === 'assistant' ? extractBrief(m.content) : null,
        }));
        if (msgs.length === 0) {
          setMessages([{
            role: 'assistant',
            content:
              "Hey — I'm CRATELAB AI Assistant. Tell me what you want to build and I'll dig through your library to construct it. Or pick a quick start below.",
          }]);
        } else {
          setMessages(msgs);
          // Find the last brief in case the user reloaded mid-flow
          const lastWithBrief = [...msgs].reverse().find((m) => m.brief);
          if (lastWithBrief) setPendingBrief(lastWithBrief.brief);
        }
      })
      .catch(() => {
        setMessages([{
          role: 'assistant',
          content:
            "Hey — I'm CRATELAB AI Assistant. Tell me what you want to build and I'll dig through your library to construct it.",
        }]);
      });
  }, [sessionId]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending, lastSet]);

  const sendMessage = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/dj/assistant/chat`, {
        session_id: sessionId,
        message: text,
      });
      const brief = extractBrief(data.reply);
      const clean = cleanReply(data.reply);
      setMessages((prev) => [...prev, { role: 'assistant', content: clean, brief }]);
      if (brief) setPendingBrief(brief);
      if (data.library_context) setLibraryCtx(data.library_context);
    } catch (err) {
      const reason = err?.response?.data?.detail || 'Assistant is offline right now.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reason, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const buildSet = async () => {
    if (!pendingBrief || building) return;
    setBuilding(true);
    try {
      const payload = {
        name: pendingBrief.set_name || pendingBrief.name,
        bpm_min: pendingBrief.bpm_min,
        bpm_max: pendingBrief.bpm_max,
        start_track_id: pendingBrief.start_track_id || null,
        target_genres: pendingBrief.target_genres || pendingBrief.genres || [],
        must_play_ids: pendingBrief.must_play_ids || [],
        duration_min: pendingBrief.duration_min || pendingBrief.duration_minutes,
        vibe: pendingBrief.vibe || pendingBrief.mood,
      };
      const { data } = await axios.post(`${API}/dj/build-set`, payload);
      setLastSet(data);
      toast.success(`Built "${data.playlist.name}" with ${data.tracks.length} tracks`);
      // Feed the result back to the assistant so it can review the set.
      const trackList = data.tracks
        .slice(0, 30)
        .map((t, i) => `${i + 1}. ${t.title || 'Untitled'} — ${
          (t.analysis?.bpm || 0).toFixed(0)
        } BPM · ${t.analysis?.camelot || '?'}`)
        .join('\n');
      sendMessage(
        `Built set: ${data.playlist.name} (${data.tracks.length} tracks)\n${trackList}\n\n` +
        `Review this set: flag the strongest run, the weakest transition, and one thing to swap.`,
      );
      setPendingBrief(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Build failed');
    } finally {
      setBuilding(false);
    }
  };

  const newChat = () => {
    const sid = newSessionId();
    localStorage.setItem(STORAGE_KEY, sid);
    setSessionId(sid);
    setMessages([]);
    setPendingBrief(null);
    setLastSet(null);
  };

  const openSession = (sid) => {
    localStorage.setItem(STORAGE_KEY, sid);
    setSessionId(sid);
    setPendingBrief(null);
    setLastSet(null);
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#0F172A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-[#DBEAFE] bg-[#EFF6FF]">
            <Sparkles className="w-3.5 h-3.5 text-[#2563FF]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#2563FF]">
              AI Assistant
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight" data-testid="assistant-title">
            Build sets from your library
          </h1>
          <p className="font-body text-sm text-[#64748B] mt-2 max-w-2xl">
            Tell the assistant what you're working on — it asks the right
            questions, reads your actual library, and constructs a playable
            set you can save and refine.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* Chat column */}
          <div className="flex flex-col rounded-2xl border border-[#E2E8F0] bg-white min-h-[60vh] shadow-[0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden">
            {/* Quick starts (only show if user hasn't said anything yet) */}
            {messages.length <= 1 && (
              <div className="px-4 pt-4 pb-2 flex flex-wrap gap-2" data-testid="assistant-quickstarts">
                {QUICK_STARTS.map((q) => (
                  <button
                    key={q.label}
                    onClick={() => sendMessage(q.prompt)}
                    className="px-3 py-1.5 rounded-full border border-[#E2E8F0] bg-white hover:border-[#2563FF]/60 hover:bg-[#EFF6FF] text-xs font-mono uppercase tracking-wider text-[#334155] transition-colors"
                    data-testid={`assistant-quickstart-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            )}

            {/* Thread */}
            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              data-testid="assistant-thread"
            >
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[88%] px-4 py-3 text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed ${
                      m.role === 'user'
                        ? 'gradient-primary text-white shadow-[0_4px_12px_rgba(37,99,255,0.35)]'
                        : m.error
                          ? 'bg-yellow-950/30 border border-yellow-700/40 text-yellow-200'
                          : 'bg-white border border-[#E2E8F0] text-[#0F172A]'
                    }`}
                    data-testid={`assistant-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </motion.div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 bg-white border border-[#E2E8F0] text-[#64748B] flex items-center gap-2 text-sm">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Assistant is thinking…
                  </div>
                </div>
              )}

              {/* Built-set summary card */}
              <AnimatePresence>
                {lastSet && (() => {
                  const setWarnings = computeSetWarnings(lastSet.tracks || []);
                  return (
                    <motion.div
                      key={lastSet.playlist.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-[#2563FF]/40 bg-[#F8FAFC] p-4"
                      data-testid="assistant-built-set"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-widest text-[#2563FF] mb-1">
                            Built · saved as playlist
                          </p>
                          <p className="font-heading text-base text-[#0F172A]">
                            {lastSet.playlist.name}
                          </p>
                          <p className="font-mono text-[10px] text-[#64748B] mt-0.5">
                            {lastSet.tracks?.length || 0} tracks
                          </p>
                        </div>
                        <Link
                          to={`/playlist/${lastSet.playlist.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A] text-xs font-heading uppercase tracking-widest flex-shrink-0"
                          data-testid="assistant-open-playlist"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open
                        </Link>
                      </div>

                      {/* Track list — collapsed to 6 rows */}
                      <div className="space-y-1 max-h-36 overflow-y-auto mb-1">
                        {lastSet.tracks.map((t, i) => (
                          <div key={t.id} className="flex items-center gap-2 text-xs">
                            <span className="font-mono text-[#94A3B8] w-5 text-right flex-shrink-0">
                              {i + 1}
                            </span>
                            <Music className="w-3 h-3 text-[#2563FF] shrink-0" />
                            <span className="text-[#0F172A] truncate flex-1">
                              {t.title || 'Untitled'}
                            </span>
                            <span className="font-mono text-[10px] text-[#64748B] shrink-0">
                              {(t.analysis?.bpm || 0).toFixed(0)} · {t.analysis?.camelot || '?'}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Mix Inspector */}
                      <MixInspectorPanel
                        tracks={lastSet.tracks}
                        warnings={setWarnings}
                        curve={lastSet.curve}
                      />
                    </motion.div>
                  );
                })()}
              </AnimatePresence>
            </div>

            {/* Build CTA */}
            {pendingBrief && !building && !lastSet && (
              <div className="px-4 pb-3" data-testid="assistant-brief-cta">
                <button
                  onClick={buildSet}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A] text-xs font-heading uppercase tracking-widest"
                  data-testid="assistant-build-btn"
                >
                  <ListPlus className="w-4 h-4" />
                  Build this set from my library
                </button>
              </div>
            )}
            {building && (
              <div className="px-4 pb-3">
                <div className="px-4 py-3 border border-[#E2E8F0] bg-[#F8FAFC] flex items-center gap-2 text-sm text-[#334155]">
                  <Loader2 className="w-4 h-4 animate-spin text-[#2563FF]" />
                  Building your set…
                </div>
              </div>
            )}

            {/* Input bar */}
            <div className="px-3 py-3 border-t border-[#E2E8F0] bg-[#F5F7FB]">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                  placeholder="Tell the assistant what you're building…"
                  className="flex-1 bg-white border border-[#E2E8F0] focus:border-[#2563FF]/60 outline-none px-3 py-2 text-sm text-[#0F172A] placeholder-gray-600 resize-none max-h-32"
                  data-testid="assistant-input"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || sending}
                  className="p-2.5 bg-[#2563FF] hover:bg-[#2563FF] disabled:bg-[#EFF6FF] disabled:text-[#94A3B8] text-[#0F172A]"
                  data-testid="assistant-send"
                  aria-label="Send"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar: library snapshot + saved chats */}
          <div className="space-y-4">
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="assistant-library-card">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#2563FF]">
                  Your Library
                </span>
                <Disc3 className="w-3.5 h-3.5 text-[#2563FF]" />
              </div>
              {libraryCtx ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">Tracks</span>
                    <span className="text-[#0F172A] font-mono">{libraryCtx.track_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">BPM range</span>
                    <span className="text-[#0F172A] font-mono">
                      {libraryCtx.bpm_min || 0}–{libraryCtx.bpm_max || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">Crates / Playlists</span>
                    <span className="text-[#0F172A] font-mono">
                      {libraryCtx.crate_count} / {libraryCtx.playlist_count}
                    </span>
                  </div>
                  {libraryCtx.top_genres?.length > 0 && (
                    <div className="pt-2 border-t border-[#E2E8F0]">
                      <p className="text-[#64748B] text-[11px] uppercase font-mono tracking-wider mb-1">Top genres</p>
                      <div className="flex flex-wrap gap-1">
                        {libraryCtx.top_genres.slice(0, 4).map((g) => (
                          <span key={g.genre} className="text-[11px] px-1.5 py-0.5 bg-[#EFF6FF] border border-[#E2E8F0] text-[#334155]">
                            {g.genre} <span className="text-[#94A3B8]">{g.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-[#64748B]">Loading…</p>
              )}
            </div>

            <div className="border border-[#E2E8F0] bg-white" data-testid="assistant-sessions">
              <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#2563FF] inline-flex items-center gap-1">
                  <History className="w-3 h-3" />
                  Recent chats
                </span>
                <button
                  onClick={newChat}
                  className="text-xs text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1"
                  data-testid="assistant-new-chat"
                >
                  <Plus className="w-3 h-3" />
                  New
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-[#E2E8F0]">
                {sessions.length === 0 && (
                  <p className="p-4 text-xs text-[#64748B] flex items-center gap-2">
                    <MessageCircle className="w-3.5 h-3.5" />
                    No saved chats yet
                  </p>
                )}
                {sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => openSession(s.session_id)}
                    className={`w-full text-left p-3 hover:bg-[#F1F5F9] transition-colors flex items-start gap-2 ${
                      s.session_id === sessionId ? 'bg-[#EFF6FF]' : ''
                    }`}
                    data-testid={`assistant-session-${s.session_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#334155] line-clamp-2">
                        {s.last_message_preview || 'New chat'}
                      </p>
                      <p className="text-[10px] text-[#94A3B8] font-mono mt-1">
                        {s.message_count} msgs
                      </p>
                    </div>
                    <ChevronRight className="w-3 h-3 text-[#475569] shrink-0 mt-1" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
